/*
 * Tab
 * @module Tabs
 * @flow
 */

import React, { isValidElement } from 'react';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { palette } from 'styled-theme';
import { NavLink } from 'react-router-dom';
import { transparentize } from 'polished';
import * as R from 'ramda';

export type TabProps = $Shape<DeviceQuery> & {
  icon?: React$Node,
  palette?: string,
  children?: React$Node,
  active?: boolean,
};

const foregroundColor = R.pipe(
  palette('grayscale', 0),
  transparentize(0.3),
);

const activeTab = css`
  color: ${palette('grayscale', 0)};
  border-bottom: 2px solid ${palette(1)};
  cursor: default;
  pointer-events: none;
`;

const TabWrapper = styled.div.attrs(() => ({
  role: 'tab',
  className: 'react-tab',
}))`
  display: inline-flex;
  align-items: center;
  color: ${foregroundColor};
  cursor: pointer;
  font-size: 14px;
  letter-spacing: 0.5px;
  pointer-events: auto;
  text-align: center;
  transition: all 250ms ease-out;

  &:hover {
    color: ${palette('grayscale', 0)};
  }

  &.react-tab__active {
    ${activeTab}
  }

  ${ifProp(
    'active',
    css`
      ${activeTab}
    `,
  )}
`;

const TabIcon = styled.div.attrs(() => ({
  className: 'react-tab-icon',
}))`
  margin-right: 5px;
  color: ${palette(1)};

  svg {
    width: 30px;
  }
`;

/**
 * Tab React-component
 * @memberof Tabs
 * @param {Object} props - properties
 * @param {React.Node<*>} [props.icon] - tab icon
 * @param {string} [props.palette = 'primary'] - current QR Code palette
 * @param {React.Children} [props.children] - children
 * @param {boolean} [props.active] - is active tab
 * @example <caption>Simple usage</caption>
 * <Tab icon={<IconWallet />}>My Wealth</Tab>
 * @returns {React.Node<*>} React-component
 */
export const Tab = ({ icon, palette, children, active, ...rest }: TabProps) => (
  <TabWrapper
    // $FlowIssue
    as={rest.to ? NavLink : null}
    active={active}
    activeClassName="react-tab__active"
    palette={palette}
    {...rest}
    testid="tab"
  >
    {isValidElement(icon) && <TabIcon palette={palette}>{icon}</TabIcon>}
    <span>{children}</span>
  </TabWrapper>
);

Tab.defaultProps = {
  palette: 'primary',
};
